import { useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { Cohort } from '../constants/experiment';

import { useIsEligibleForRtSuppression } from './useIsEligibleForRtSuppression';

const { getExperimentValue, checkGate } = FeatureGates;

interface UseExperimentEnrollmentProps {
	experimentId: string;
	featureGateId: string;
	skip?: boolean;
	extraAnalyticsAttributes?: object;
}

interface UseExperimentEnrollmentReturn {
	isTreatment: boolean;
	loading: boolean;
	error: string | null | undefined;
	daysLeftInTrial?: number;
	isInTrialOrPreDunning?: boolean;
}

export const useExperimentEnrollment = ({
	experimentId,
	featureGateId,
	skip = false,
	extraAnalyticsAttributes = {},
}: UseExperimentEnrollmentProps): UseExperimentEnrollmentReturn => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isFeatureGateEnabled = checkGate(featureGateId);
	const {
		isEligible,
		daysLeftInTrial,
		isInTrialOrPreDunning,
		loading,
		error,
		analyticsAttributes,
	} = useIsEligibleForRtSuppression({
		experimentId,
		skip: skip || !isFeatureGateEnabled,
		extraAnalyticsAttributes,
	});

	const experimentValues = useMemo(() => {
		if (isEligible && !loading && !error) {
			const cohort = getExperimentValue(experimentId, 'cohort', Cohort.NOT_ENROLLED);

			if (cohort !== Cohort.NOT_ENROLLED) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'exposed',
						actionSubject: 'experiment',
						actionSubjectId: `${experimentId}-exposed`,
						attributes: {
							cohort,
							...extraAnalyticsAttributes,
							...analyticsAttributes,
						},
						source: experimentId,
					},
				}).fire();
			}

			return {
				isTreatment: cohort === Cohort.TREATMENT,
				loading,
				error,
				daysLeftInTrial,
				isInTrialOrPreDunning,
			};
		}

		return {
			isTreatment: false,
			loading,
			error,
			daysLeftInTrial,
			isInTrialOrPreDunning,
		};
		// Using primitives for dependencies to avoid unnecessary re-renders
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEligible, experimentId, loading, error]);

	return experimentValues;
};
